import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Paper, Breadcrumbs, Grid, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { LockOpen as LockOpenIcon, NavigateNext as NavigateNextIcon, ManageAccounts as ManageAccountsIcon } from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import {
    unlockAccount,
    fetchLockedAccounts,
    selectLockedAccounts,
    selectAccountManagementLoading,
    selectAccountManagementError,
    selectAccountManagementSuccessMessage,
    clearError,
    clearSuccessMessage,
} from './slice';

function AccountManagement() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const userIdInputRef = useRef(null);

    const lockedAccounts = useSelector(selectLockedAccounts);
    const loading = useSelector(selectAccountManagementLoading);
    const error = useSelector(selectAccountManagementError);
    const successMessage = useSelector(selectAccountManagementSuccessMessage);

    const [unlockUserId, setUnlockUserId] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [unlockDialog, setUnlockDialog] = useState({ open: false, userId: null });
    const [userIdError, setUserIdError] = useState('');

    const formatLockoutEndDate = (value) => {
        if (!value) {
            return t('accountManagement.lockedNoExpiry');
        }

        const withTimezone = value.includes('Z') ? value : `${value}Z`;
        const date = new Date(withTimezone);

        if (Number.isNaN(date.getTime())) {
            return t('accountManagement.lockedNoExpiry');
        }

        const timeFormatter = new Intl.DateTimeFormat('vi-VN', {
            timeZone: 'Asia/Ho_Chi_Minh',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        const dateFormatter = new Intl.DateTimeFormat('vi-VN', {
            timeZone: 'Asia/Ho_Chi_Minh',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });

        return `${timeFormatter.format(date)} ${dateFormatter.format(date)}`;
    };

    useEffect(() => {
        dispatch(fetchLockedAccounts({ keyword: '' }));
    }, [dispatch]);

    // Validate User ID
    const isValidUserId = (value) => {
        if (!value || value.trim() === '') {
            return false;
        }
        const trimmed = value.trim();
        // Only allow digits
        if (!/^\d+$/.test(trimmed)) {
            return false;
        }
        const num = parseInt(trimmed, 10);
        return !isNaN(num) && num > 0;
    };

    // Show error notification
    useEffect(() => {
        if (error) {
            setUserIdError(error);
            if (userIdInputRef.current) {
                userIdInputRef.current.focus();
            }
            dispatch(clearError());
        }
    }, [error, dispatch]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            dispatch(fetchLockedAccounts({ keyword: searchKeyword.trim() }));
        }
    }, [successMessage, dispatch, showNotification, searchKeyword]);

    const handleUserIdChange = (e) => {
        const value = e.target.value;
        // Filter out non-numeric characters
        const numericValue = value.replace(/[^\d]/g, '');
        setUnlockUserId(numericValue);

        // Clear error when user starts typing
        if (userIdError) {
            setUserIdError('');
        }
    };

    const handleUnlockAccount = () => {
        const trimmed = unlockUserId.trim();

        if (!trimmed) {
            setUserIdError(t('accountManagement.error.invalidUserIdHelper'));
            showNotification(t('accountManagement.error.invalidUserId'), 'error');
            if (userIdInputRef.current) {
                userIdInputRef.current.focus();
            }
            return;
        }

        if (!isValidUserId(trimmed)) {
            setUserIdError(t('accountManagement.error.invalidUserIdHelper'));
            showNotification(t('accountManagement.error.invalidUserId'), 'error');
            if (userIdInputRef.current) {
                userIdInputRef.current.focus();
            }
            return;
        }

        const userId = parseInt(trimmed, 10);
        setUserIdError('');
        setUnlockDialog({ open: true, userId });
    };

    const confirmUnlock = () => {
        dispatch(unlockAccount(unlockDialog.userId));
        setUnlockDialog({ open: false, userId: null });
        setUnlockUserId('');
        setUserIdError('');
    };

    const handleSearchLockedAccounts = () => {
        dispatch(fetchLockedAccounts({ keyword: searchKeyword.trim() }));
    };

    const handleUnlockFromTable = (row) => {
        setUnlockDialog({ open: true, userId: row.userId });
    };

    const columns = [
        { key: 'userId', label: t('accountManagement.table.userId'), width: '150px', textAlign: 'start' },
        { key: 'username', label: t('accountManagement.table.username') },
        { key: 'email', label: t('accountManagement.table.email') },
        { key: 'fullName', label: t('accountManagement.table.fullName') },
        {
            key: 'failedLoginAttempts',
            label: t('accountManagement.table.failedAttempts'),
        },
        {
            key: 'lockoutEndDate',
            label: t('accountManagement.table.lockoutEndDate'),
            render: (value) => formatLockoutEndDate(value),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#f5f5f5' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff', borderBottom: '1px solid #e0e0e0' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ManageAccountsIcon fontSize="small" />
                        <Typography variant="body2">{t('accountManagement.breadcrumb.users')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('accountManagement.breadcrumb.accountManagement')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title */}
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                    {t('accountManagement.title')}
                </Typography>
            </Box>

            {/* Main Content */}
            <Box sx={{ px: 3, pt: 3, pb: 3, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
                    <Box sx={{ flex: 1 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                height: '100%',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: '#e3f2fd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <LockOpenIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="600">
                                        {t('accountManagement.unlockAccount')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Mở khóa tài khoản bị khóa do đăng nhập sai
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    inputRef={userIdInputRef}
                                    label={t('accountManagement.userIdLabel')}
                                    placeholder={t('accountManagement.userIdPlaceholder')}
                                    value={unlockUserId}
                                    onChange={handleUserIdChange}
                                    inputMode="numeric"
                                    pattern="\d+"
                                    fullWidth
                                    size="medium"
                                    id="unlockUserId"
                                    name="unlockUserId"
                                    data-testid="accountManagement-input-unlockUserId"
                                    error={!!userIdError}
                                    helperText={userIdError || t('accountManagement.error.invalidUserIdHelper')}
                                    aria-invalid={!!userIdError}
                                    aria-describedby="unlockUserId-helper-text"
                                    FormHelperTextProps={{ id: 'unlockUserId-helper-text' }}
                                    onKeyDown={(e) => {
                                        if (
                                            !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) &&
                                            !/[0-9]/.test(e.key) &&
                                            !e.ctrlKey &&
                                            !e.metaKey
                                        ) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<LockOpenIcon />}
                                    onClick={handleUnlockAccount}
                                    disabled={loading || !isValidUserId(unlockUserId)}
                                    fullWidth
                                    sx={{ py: 1.2 }}
                                    id="unlockButton"
                                    name="unlockButton"
                                    data-testid="accountManagement-button-unlockButton"
                                >
                                    {loading ? t('common.loading') : t('accountManagement.unlockAccount')}
                                </Button>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                height: '100%',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: '#fff3e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ManageAccountsIcon sx={{ color: '#f57c00', fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="600">
                                        {t('accountManagement.lockedAccounts')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('accountManagement.lockedAccountsDescription')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label={t('accountManagement.searchKeywordLabel')}
                                    placeholder={t('accountManagement.searchKeywordPlaceholder')}
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearchLockedAccounts();
                                        }
                                    }}
                                    fullWidth
                                    size="medium"
                                    id="lockedSearch"
                                    name="lockedSearch"
                                    data-testid="accountManagement-input-lockedSearch"
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSearchLockedAccounts}
                                    disabled={loading}
                                    fullWidth
                                    sx={{ py: 1.2 }}
                                    id="fetchButton"
                                    name="fetchButton"
                                    data-testid="accountManagement-button-fetchButton"
                                >
                                    {t('accountManagement.fetchLockedAccounts')}
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Box>

                <Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography variant="h6" gutterBottom fontWeight="600" sx={{ textAlign: 'left' }}>
                            {t('accountManagement.lockedAccounts')}
                        </Typography>
                        <Table
                            id="accountManagement-table"
                            columns={columns}
                            data={lockedAccounts}
                            loading={loading}
                            pagination={false}
                            emptyMessage={t('accountManagement.noData')}
                        />
                    </Paper>
                </Box>
            </Box>

            {/* Unlock Confirmation Dialog */}
            <Dialog open={unlockDialog.open} onClose={() => setUnlockDialog({ open: false, userId: null })}>
                <DialogTitle>{t('accountManagement.unlockDialog.title')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('accountManagement.unlockDialog.message')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setUnlockDialog({ open: false, userId: null })}
                        id="unlockCancel"
                        name="unlockCancel"
                        data-testid="accountManagement-button-unlockCancel"
                    >
                        {t('accountManagement.unlockDialog.cancel')}
                    </Button>
                    <Button
                        onClick={confirmUnlock}
                        variant="contained"
                        color="primary"
                        id="unlockConfirm"
                        name="unlockConfirm"
                        data-testid="accountManagement-button-unlockConfirm"
                    >
                        {t('accountManagement.unlockDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AccountManagement;
